import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@instructure/ui-buttons';
import { CSVLink } from 'react-csv';
import { Flex } from '@instructure/ui-flex';
import { ProgressBar } from '@instructure/ui-progress';
import { Text } from '@instructure/ui-text';
import { Transition } from '@instructure/ui-motion';
import t from 'format-message';
import useBoolean from "../../../hooks/useBoolean.js";
import useCSVExport, { NOT_EXPORTING, CSV_COMPLETE } from "../../../hooks/useCSVExport.js";
import { STUDENT_NAME, STUDENT_UUID, ATTEMPT_COUNT, ALIGNED_QUESTIONS_COUNT, LOR_POINTS, LOR_POINTS_POSSIBLE, LO_NAME, LO_ID, LO_MASTERY_PERCENT, LO_MASTERED } from "../../../constants.js";
import { fileName, formatPercentage } from "../../../util/outcomesReportUtils.js";
export var headers = [{
  label: t('Student Name'),
  key: STUDENT_NAME
}, {
  label: t('Student UUID'),
  key: STUDENT_UUID
}, {
  label: t('Number of Attempts'),
  key: ATTEMPT_COUNT
}, {
  label: t('Number of Aligned Questions'),
  key: ALIGNED_QUESTIONS_COUNT
}, {
  label: t('Learning Outcome Result Points'),
  key: LOR_POINTS
}, {
  label: t('Learning Outcome Result Points Possible'),
  key: LOR_POINTS_POSSIBLE
}, {
  label: t('Learning Outcome Name'),
  key: LO_NAME
}, {
  label: t('Learning Outcome ID'),
  key: LO_ID
}, {
  label: t('Learning Outcome Mastery Percent'),
  key: LO_MASTERY_PERCENT
}, {
  label: t('Learning Outcome Mastered'),
  key: LO_MASTERED
}];
var ExportCSVButton = function ExportCSVButton(_ref) {
  var fetchCSVData = _ref.fetchCSVData,
    formatCSVData = _ref.formatCSVData,
    fetchingStatus = _ref.fetchingStatus,
    artifactId = _ref.artifactId,
    focusedElement = _ref.focusedElement;
  var csvElementRef = useRef(null);
  var exportCSV = function exportCSV() {
    var _csvElementRef$curren;
    return (_csvElementRef$curren = csvElementRef.current) === null || _csvElementRef$curren === void 0 ? void 0 : _csvElementRef$curren.click();
  };
  var _useBoolean = useBoolean(false),
    _useBoolean2 = _slicedToArray(_useBoolean, 3),
    isShowingProgressBar = _useBoolean2[0],
    showProgressBar = _useBoolean2[1],
    hideProgressBar = _useBoolean2[2];
  var _useState = useState(useRef(null)),
    _useState2 = _slicedToArray(_useState, 2),
    csvButtonRef = _useState2[0],
    setCsvButtonRef = _useState2[1];
  var _useCSVExport = useCSVExport({
      fetchCSVData: fetchCSVData,
      fetchingStatus: fetchingStatus,
      formatCSVData: formatCSVData,
      showProgressBar: showProgressBar,
      hideProgressBar: hideProgressBar
    }),
    beginExport = _useCSVExport.beginExport,
    cancelExport = _useCSVExport.cancelExport,
    exportState = _useCSVExport.exportState,
    progressValue = _useCSVExport.progressValue,
    formattedData = _useCSVExport.formattedData;
  var canStartExport = exportState === NOT_EXPORTING;
  useEffect(function () {
    if (exportState === CSV_COMPLETE) {
      exportCSV();
      setTimeout(function () {
        if (focusedElement === csvButtonRef) {
          csvButtonRef.focus();
        }
      }, 3000);
    }
  }, [exportState]);
  return /*#__PURE__*/React.createElement(Flex, {
    as: "div",
    width: "100%",
    alignItems: "center",
    justifyItems: "space-between"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      textAlign: 'start'
    }
  }, /*#__PURE__*/React.createElement(Transition, {
    in: isShowingProgressBar,
    type: "fade",
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "bold",
    size: "small",
    color: "primary"
  }, t('Exporting')), /*#__PURE__*/React.createElement(ProgressBar, {
    screenReaderLabel: t('Export CSV Percent Complete'),
    renderValue: function renderValue(_ref2) {
      var valueNow = _ref2.valueNow,
        valueMax = _ref2.valueMax;
      return t('{percentDone}%', {
        percentDone: formatPercentage(valueNow, valueMax)
      });
    },
    formatScreenReaderValue: function formatScreenReaderValue(_ref3) {
      var valueNow = _ref3.valueNow,
        valueMax = _ref3.valueMax;
      return t('{percentDone} percent', {
        percentDone: formatPercentage(valueNow, valueMax)
      });
    },
    valueNow: progressValue,
    "data-automation": "outcomesPerStudent_exportCSVProgressBar",
    margin: "0 0 xx-small"
  }))), /*#__PURE__*/React.createElement(Button, {
    margin: "medium 0 medium 0",
    color: canStartExport ? 'primary' : 'secondary',
    onClick: canStartExport ? beginExport : cancelExport,
    interaction: canStartExport ? 'enabled' : progressValue < 100 ? 'enabled' : 'disabled',
    "data-automation": "outcomesPerStudent_exportCSVButton",
    elementRef: function elementRef(ref) {
      return setCsvButtonRef(ref);
    }
  }, canStartExport ? t('Export CSV') : t('Cancel Export')), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(CSVLink, {
    data: formattedData,
    headers: headers,
    filename: fileName(artifactId),
    tabIndex: -1
  }, /*#__PURE__*/React.createElement("span", {
    ref: csvElementRef
  }))));
};
ExportCSVButton.propTypes = {
  fetchCSVData: PropTypes.func.isRequired,
  formatCSVData: PropTypes.func.isRequired,
  fetchingStatus: PropTypes.string.isRequired,
  artifactId: PropTypes.string.isRequired,
  focusedElement: PropTypes.element
};
ExportCSVButton.defaultProps = {
  focusedElement: null
};
export default ExportCSVButton;